import { useCallback, useEffect, useMemo, useState } from 'react';
import { deleteUserEmail, emailRegex, FormField, handleRequestError, InfoBox, Logger, registerUserEmail, useUser, confirm, FWKIcons, Loading, toast, getUserEmail, useUserEmail, fetchUser, useFeatureIsEnabled } from '@desp-aas/desp-ui-fwk';
import classNames from 'classnames';
import './UpdateUserEmail.css';
import { useDispatch } from 'react-redux';

const logger = new Logger("component", "UpdateUserEmail");

const emailIsValid = (email: string|undefined) => {
    return !!email && emailRegex.test(email);
}

export const UpdateUserEmail = () =>{   
    const user = useUser();
    const dispatch = useDispatch();
    const currentEmail = useUserEmail();
    
    const [ email, setEmail ] = useState<string|undefined>(getUserEmail(user));
    const [ registeringEmail, setRegisteringEmail ] = useState<string|undefined>();
    const [ updating, setUpdating ] = useState(false);
    
    useEffect(()=>{
        setEmail(getUserEmail(user));
    }, [ user?.email ])
    
    const emailUpdated = useMemo(()=>{
        return emailIsValid(email);
    }, [ email ]);
    
    const emailToUse = useMemo(()=>{
        return registeringEmail || email;
    }, [ registeringEmail, email ]);
    
    const handleUpdateEmail = useCallback(async () => {
        if(!email || !emailUpdated) return;
        setUpdating(true);
        try {
            await registerUserEmail(email, window.location.href);
            // toast.success(<span>Email registration request sent to your inbox at <b>{email}</b>. <br/><br/> Please check your email to complete the registration.</span>);
            
            setRegisteringEmail(email);
            fetchUser(dispatch);
        } catch (error: any) {
            logger.error('Error updating user email', error);
            handleRequestError(error, { defaultMessage: 'An error occurred while updating user email' });
        }
        finally{
            setUpdating(false);
        }
    }, [ email, dispatch ]);
    const handleDeleteEmail = useCallback(async () => {
        if(!email || !emailUpdated) return;
        
        confirm({
            title: 'Delete email',
            message: <>
                Are you sure you want to delete your email? This action cannot be undone.
            </>,
            onConfirm: async () => {
                setUpdating(true);
                try {
                    await deleteUserEmail();
                    toast.success(<span>Email <b>{email}</b> successfully deleted</span>);
                    fetchUser(dispatch);
                } catch (error: any) {
                    logger.error('Error deleting user email', error);
                    handleRequestError(error, { defaultMessage: 'An error occurred while deleting user email' });
                }
                finally{
                    setUpdating(false);
                }
            }
        })
        
        
    }, [ email ]);
    
    const isEmailEnabled = useFeatureIsEnabled("email_enabled");

    if(!isEmailEnabled){
        return null;
    }

    return (
        <>
            <div className="update-user-email">
                <FormField
                    label="Email"
                    value={emailToUse}
                    disabled={!!getUserEmail(user) || !!registeringEmail || user?.desp_profile?.email_read_only}
                    onUpdate={(email)=>{ setEmail(email) }}
                    onEnter={ !!getUserEmail(user) ? handleDeleteEmail : handleUpdateEmail}
                    validation={[
                        {
                            description: 'Email format is not valid',
                            validation: (value) => {
                                return emailIsValid(value);
                            }
                        }
                    ]}
                    operations={
                        registeringEmail || user?.desp_profile?.email_read_only
                        ? undefined
                        :
                        !!currentEmail ? 
                            
                            <div 
                                className={classNames("button form-field-button small red inverted", {
                                    "disabled": !emailUpdated || updating
                                })}
                                onClick={handleDeleteEmail}
                            >
                                {
                                    updating 
                                    ? <><Loading/> Deleting...</>
                                    : <>{FWKIcons.delete} Delete</>
                                }
                                
                            </div>
                        :
                        <div 
                            className={classNames("button form-field-button small blue inverted", {
                                "disabled": !emailUpdated || updating
                            })}
                            onClick={handleUpdateEmail}
                        >
                            {
                                updating 
                                ? <><Loading/> Registering email...</>
                                : <>{FWKIcons.edit} Register email</>
                            }
                        </div>
                    }
                />
            </div>
            {
                registeringEmail &&
                <>
                    <InfoBox
                        title="Email registration request sent"
                        type="success"
                    >
                        Email registration request sent to your inbox at <b>{registeringEmail}</b>. <br/><br/>Please check your email to complete the registration.
                    </InfoBox>
                    <br/>
                </>
            }
        </>

    )
}